/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import React from "react";
import {
    IconCube3d,
    IconMaterialType,
    PersonChip
} from "../../../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../../../components/CustomChip";
import CustomFileCard from "../../../../../../../components/CustomFileCard";
import NotInformation from "../../../../../../../components/NotInformation";
import {colors} from "../../../../../../../enums/colors";
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    downloadActionFile,
    getDateFormat,
    sanitizeString
} from "../../../../../../../Apis/functions";
import {FILE_MODEL} from "../../../../../Visitor/Visitor/Model/Model";
import {getUnitTransByName} from "../../../../../Dining/Ingredients/Model/Models";
import {getMaterialControlItemTypeTab} from "../../../../Model/Model";
import DispatchedMaterialRow from "./DispatchedMaterialRow";
import EntryAndExitFormDiscrepancyCard from "../EntryAndExitFormDiscrepancyCard";

/**
 * Way the materials of the movement are read.
 *
 * - EVIDENCE: what was asked for, with the evidence uploaded for it once the
 *   movement was closed.
 * - CARD: the review of every material, because the movement was dispatched or
 *   has to be attended before it can be.
 * - DISPATCHED: the direction every material took at the gate.
 */
export const MATERIALS_MODE = {
    EVIDENCE: 'EVIDENCE',
    CARD: 'CARD',
    DISPATCHED: 'DISPATCHED'
};

/**
 * Material asked for in the movement, with the evidence uploaded for it.
 *
 * The evidence is only read once the guard validated the material at the gate,
 * because it is the guard who uploads it.
 */
const MaterialEvidenceCard = ({t, item, dispatched}) => {
    // A material captured with a quantity is read with it, and the one that is
    // only described is read with the direction it takes at the plant
    const quantity = item?.quantity != null
        ? `${item.quantity} ${t(getUnitTransByName(item?.measurementUnitName))}`
        : null;
    const typeTab = getMaterialControlItemTypeTab(item?.type);
    const fileData = item?.itemEvidence || null;
    const file = item?.itemEvidence ? FILE_MODEL.set(item.itemEvidence) : null;
    const showEvidence = dispatched && fileData;

    return (
        <div className={"row border-radius-8px background-shades-100 border-full-shades-100"}>
            <div className={"col-12 p-3"}>
                <div className={"d-flex justify-content-between"}>
                    <div className={"d-flex align-items-center"}>
                        <div className={"background-primary-25 border-radius-8px mr-2 p-2"}>
                            <IconCube3d width={30} height={30}/>
                        </div>
                        <span className={"ibl-font-w-600 fs-14px"}>{item?.name}</span>
                    </div>
                    {(quantity || typeTab) &&
                        <div>
                            <div className={"fs-12px d-flex justify-content-end"}>
                                {t(quantity ? "quantity" : "type")}
                            </div>
                            <CustomChip
                                background={colors.secondary100}
                                fontColor={colors.secondary700}
                                borderColor={colors.secondary150}
                                legend={quantity || t(typeTab.translation)}
                                icon={<IconMaterialType width={14} height={14}/>}
                                className='icon-color-secondary-700'
                            />
                        </div>
                    }
                </div>
            </div>

            {showEvidence && <>
                <div className={"col-12"}>
                    <div className='border-bottom-shades-300 mb-3'/>
                </div>

                <div className={"col-12 mb-3"}>
                    <div className={"row background-shades-100 border-full-shades-300 border-radius-8px p-3"}>
                        <div className={"d-flex p-0"}>
                            <div className={"mr-3"}>
                                <PersonChip className={"icon-color-shades-400"}/>
                            </div>
                            <div className={"w-100"}>
                                <div>
                                    <div className={"ibl-font-w-600 fs-14px"}>{fileData?.uploadedBy}</div>
                                    <div className={"mt-1 mb-3 fs-14px"}>
                                        {getDateFormat(fileData?.createdAt, null, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                                    </div>
                                </div>

                                <div className={"background-general-00 border-full-shades-300 border-radius-8px p-2 w-100 mb-3"}>
                                    {sanitizeString(fileData?.note)}
                                </div>

                                <div>
                                    {file && <CustomFileCard
                                        classes={"border-0"}
                                        classContent={"background-shades-50 border-radius-8px border-full-shades-300"}
                                        Icon={file?.Icon}
                                        title={file?.name}
                                        fileValue={file?.filePath}
                                        subtitle={`${file.size} ${t('uploaded')}: ${getDateFormat(fileData?.createdAt)}`}
                                        showChip={false}
                                        buttonId={"btn_ms_download_" + item?.id}
                                        onClick={() => downloadActionFile(file?.filePath)}
                                    />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
            }
        </div>
    );
};

/**
 * Materials of the movement, read the way the status of the folio asks for.
 *
 * @returns {JSX.Element} List of materials, or the empty state while none is
 * added to the movement.
 */
const MaterialsSection = ({
                              t,
                              item: folio,
                              items = [],
                              mode = MATERIALS_MODE.EVIDENCE,
                              dispatched = false,
                              registeredBy,
                              registeredAt
                          }) => {

    // A movement can be registered with no materials at all, so the list says
    // that none was written instead of reading as a list with no records
    if (!items.length)
        return <NotInformation text={t('no_materials_registered')} card={true}/>;

    return (
        <>
            {items.map((item, index) => {
                const key = item?.id ?? index;

                if (mode === MATERIALS_MODE.DISPATCHED)
                    return (
                        <div className={"mb-3"} key={key}>
                            <DispatchedMaterialRow
                                t={t}
                                folio={folio}
                                item={item}
                                registeredBy={registeredBy}
                                registeredAt={registeredAt}
                            />
                        </div>
                    );

                if (mode === MATERIALS_MODE.CARD)
                    return (
                        <div className={"mb-3"} key={key}>
                            <EntryAndExitFormDiscrepancyCard
                                t={t}
                                item={item}
                                file={item?.itemEvidence ? FILE_MODEL.set(item.itemEvidence) : null}
                                dispatched={dispatched}
                            />
                        </div>
                    );

                return (
                    <div className={"mb-3"} key={key}>
                        <MaterialEvidenceCard
                            t={t}
                            item={item}
                            dispatched={dispatched}
                        />
                    </div>
                );
            })}
        </>
    );
};

export default MaterialsSection;
